import {
  WsmFitmentProductApplicationFragment,
  WsmFitmentProductAxisFragment,
  WsmFitmentProductSchemaFragment,
} from "@dashboard/graphql";
import { Option } from "@saleor/macaw-ui-next";

/** A schema's axes, lowest position first: the order a merchant reads an application in. */
export const schemaAxesInOrder = (schema: WsmFitmentProductSchemaFragment) =>
  [...schema.axes].sort((a, b) => a.position - b.position).map(schemaAxis => schemaAxis.axis);

/**
 * One line for one application: a term per schema axis in the given order
 * (from `schemaAxesInOrder`, which the caller memoises per schema so it is
 * not re-sorted per row), then its qualifier values as "Qualifier: value".
 * An axis the application leaves unconstrained prints `anyLabel(axis)`
 * ("any Make"), so "2019 any Make F-350" is never read as a two-axis point.
 * A value on an axis the schema no longer lists is appended so nothing is
 * hidden.
 */
export const formatApplication = (
  application: Pick<WsmFitmentProductApplicationFragment, "values" | "qualifierValues">,
  axes: ReadonlyArray<Pick<WsmFitmentProductAxisFragment, "id" | "name" | "displayLabel">>,
  anyLabel: (axisLabel: string) => string = axisLabel => `any ${axisLabel}`,
): string => {
  const byAxis = new Map(
    application.values.map(value => [value.axis.id, value.value] as [string, string]),
  );
  const listed = new Set(axes.map(axis => axis.id));
  const terms = axes.map(axis => byAxis.get(axis.id) ?? anyLabel(axis.displayLabel || axis.name));
  const extra = application.values
    .filter(value => !listed.has(value.axis.id))
    .map(value => value.value);
  const qualifiers = application.qualifierValues.map(
    qualifierValue =>
      `${qualifierValue.qualifier.displayLabel || qualifierValue.qualifier.name}: ${qualifierValue.value}`,
  );

  return [...terms, ...extra, ...qualifiers].join(" ");
};

/** Numeric-aware label order: "2" before "10", case folded by the locale. */
export const compareLabels = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/**
 * An application's qualifier values hold at most one value per qualifier
 * (wsm/fitment README, `get_or_create_application`). Given the selection
 * before and after a multiselect change, a newly picked value replaces any
 * earlier value of the same qualifier; a value whose qualifier is unknown
 * is kept as is.
 */
export const onePerQualifier = (
  previous: readonly Option[],
  next: readonly Option[],
  qualifierOf: ReadonlyMap<string, string>,
): Option[] => {
  const before = new Set(previous.map(option => option.value));
  const kept = new Map<string, Option>();
  const loose: Option[] = [];

  // Earlier picks first, then the new ones, so a new pick wins its qualifier.
  [
    ...next.filter(option => before.has(option.value)),
    ...next.filter(option => !before.has(option.value)),
  ].forEach(option => {
    const qualifier = qualifierOf.get(option.value);

    if (qualifier === undefined) {
      loose.push(option);
    } else {
      kept.set(qualifier, option);
    }
  });

  const winners = new Set([...loose, ...kept.values()].map(option => option.value));

  // Keep the multiselect's own order for what survives.
  return next.filter(option => winners.has(option.value));
};

/** Slices a list into runs of at most `size`: the per-call cap on `ids`. */
export const chunk = <T>(items: T[], size: number): T[][] => {
  // A size below one would never advance the loop.
  if (!Number.isInteger(size) || size < 1) {
    throw new RangeError(`chunk size must be a positive integer, got ${size}`);
  }

  const chunks: T[][] = [];

  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }

  return chunks;
};

/**
 * Order-insensitive equality of two id lists as sets: a repeated id counts
 * once, so ["a", "a"] and ["a", "b"] differ.
 */
export const sameIdSet = (a: string[], b: string[]): boolean => {
  const aSet = new Set(a);
  const bSet = new Set(b);

  if (aSet.size !== bSet.size) {
    return false;
  }

  return Array.from(aSet).every(id => bSet.has(id));
};

/**
 * A content key for an id list: the distinct ids, sorted and joined. Reset
 * effects key on this rather than on the array, because a refetch hands back
 * a new array (no ids on the fitment rows, so Apollo rebuilds the tree) with
 * the same content, and that must not discard a card's unsaved edits.
 */
export const idSetKey = (ids: readonly string[]): string =>
  Array.from(new Set(ids)).sort().join(",");

/**
 * Appends one fetched page of a connection to the pages already held: the
 * edges concatenated, the page info taken from the newer page.
 */
export const appendConnection = <C extends { edges: readonly unknown[] }>(
  held: C | null | undefined,
  page: C | null | undefined,
): C | null => {
  if (!held || !page) {
    return page ?? held ?? null;
  }

  return { ...page, edges: [...held.edges, ...page.edges] };
};

/**
 * Client-side filter for the qualifier multiselects, whose vocabulary arrives
 * whole (nested under wsmFitmentQualifiers). Case-insensitive substring; an
 * empty query keeps everything.
 */
export const filterOptions = (options: Option[], query: string): Option[] => {
  const needle = query.trim().toLowerCase();

  if (!needle) {
    return options;
  }

  return options.filter(option => option.label.toLowerCase().includes(needle));
};

/**
 * The model's own sentence, else the code; the field first when there is one.
 * IN_USE and the rest arrive this way from every fitment mutation.
 */
export const formatWsmError = (error: {
  field?: string | null;
  message?: string | null;
  code: string;
}) => `${error.field ? `${error.field}: ` : ""}${error.message || error.code}`;

/** "Qualifier: value" options over the whole qualifier vocabulary. */
export const qualifierValueOptions = (
  qualifiers: Array<{
    name: string;
    displayLabel: string;
    values: Array<{ id: string; value: string }>;
  }>,
): Option[] =>
  qualifiers.flatMap(qualifier =>
    qualifier.values.map(value => ({
      label: `${qualifier.displayLabel || qualifier.name}: ${value.value}`,
      value: value.id,
    })),
  );

/** Qualifier value id to its qualifier's id, over the whole vocabulary. */
export const qualifierOfValue = (
  qualifiers: Array<{ id: string; values: Array<{ id: string }> }>,
): Map<string, string> =>
  new Map(
    qualifiers.flatMap(qualifier =>
      qualifier.values.map(value => [value.id, qualifier.id] as [string, string]),
    ),
  );
